import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";

import { type Price } from "@repo/apis/browse-search-v2";

import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { formatPrice } from "@repo/utils/currency-formatter";
import classes from "./product-price.module.css";

const getPercentage = (percentage: number | null) => {
  if (percentage !== null) {
    const adjustedDiscountPercentage = percentage * -1;
    const sign = adjustedDiscountPercentage > 0 ? "+" : "";
    return `${sign}${adjustedDiscountPercentage}%`;
  }

  return "";
};

export type ProductPriceProps = HTMLAttributes<HTMLParagraphElement> & {
  asChild?: boolean;
  priceData: Price;
  country: string;
  showDiscount?: boolean;
  previousPriceCopy: string;
  currentPriceCopy: string;
  currency: string;
};

export const ProductPrice = forwardRef<HTMLParagraphElement, ProductPriceProps>(
  (
    {
      priceData,
      country,
      showDiscount = false,
      previousPriceCopy,
      currentPriceCopy,
      currency,
      ...props
    },
    ref,
  ) => {
    const { current, previous, isMarkedDown, discountPercentage } = priceData;

    const formattedCurrentPrice = formatPrice({
      value: current.value ?? 0,
      countryCode: country,
      currencyCode: currency,
    });

    const formattedPreviousPrice = isMarkedDown
      ? formatPrice({
          value: previous.value ?? 0,
          countryCode: country,
          currencyCode: currency,
        })
      : null;

    const qualifiesToShowPercentage =
      showDiscount && discountPercentage !== 0 && isMarkedDown;

    return (
      <p ref={ref} {...props}>
        {isMarkedDown ? (
          <>
            <span className={clsx(classes.previousPrice)}>
              <VisuallyHidden>{previousPriceCopy}</VisuallyHidden>
              {formattedPreviousPrice}
            </span>
          </>
        ) : null}
        <VisuallyHidden>{currentPriceCopy}</VisuallyHidden>
        <span
          className={clsx(
            clsx(classes.price),
            isMarkedDown && clsx(classes.markedDown),
          )}
        >
          {" "}
          {formattedCurrentPrice}
        </span>

        {qualifiesToShowPercentage && (
          <span className={clsx(classes.discount)}>
            {getPercentage(discountPercentage)}
          </span>
        )}
      </p>
    );
  },
);

ProductPrice.displayName = "ProductPrice";
